import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Alert, AlertDescription, AlertTitle } from '@voilajsx/uikit/alert';
import { UserPlus, ArrowLeft, AlertTriangle, CheckCircle, Info, X, Shield, Crown, UserCircle, Eye, Coins } from 'lucide-react';
import { Header, SEO } from '../../../../shared/components';
import { AdminSidebar } from '../../../admin/components/AdminSidebar';
import { AuthGuard } from '../../../auth';
import { useAuth } from '../../../auth';
import { route, hasRole } from '../../../../shared/utils';
import { config } from '../../../auth/config';
import { USER_ROLES } from '../../index';

const RBAC_ROLES = [
  {
    label: 'System Admin', color: '#ef4444', icon: Crown,
    permissions: [
      'Full platform access',
      'Manage all users & roles',
      'Configure services & pricing',
      'View financial data & payments',
      'Grant/revoke credits',
      'View audit logs',
      'Manage app settings',
    ],
  },
  {
    label: 'Moderator', color: '#f59e0b', icon: Shield,
    permissions: [
      'View all users',
      'View services (no pricing)',
      'View credit log (no financials)',
      'View audit logs',
      'Cannot manage settings or packs',
    ],
  },
  {
    label: 'User', color: '#a1a1aa', icon: UserCircle,
    permissions: [
      'Access all services',
      'Save & manage designs',
      'Download with credits',
      'View own credit history',
      'View own download history',
    ],
  },
];

const RbacPopup: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999,
        backdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: '#141414', border: '1px solid #2a2a2a', borderRadius: 14,
        width: '100%', maxWidth: 560, maxHeight: '80vh', overflowY: 'auto',
        zIndex: 1000, padding: 0,
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #222',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#141414', zIndex: 1,
        }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>About Roles</h3>
            <p style={{ fontSize: 12, color: '#666', margin: '2px 0 0' }}>What each role can access</p>
          </div>
          <button onClick={onClose} style={{
            background: '#1a1a1a', border: '1px solid #333', borderRadius: 8,
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#888',
          }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {RBAC_ROLES.map((role) => {
            const Icon = role.icon;
            return (
              <div key={role.label} style={{
                background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: `${role.color}15`, border: `1px solid ${role.color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon style={{ width: 16, height: 16, color: role.color }} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: role.color }}>{role.label}</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {role.permissions.map((perm) => (
                    <li key={perm} style={{ fontSize: 13, color: '#999', lineHeight: 1.4 }}>{perm}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const CreateUserPage: React.FC = () => {
  const { user, token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showRbac, setShowRbac] = useState(false);
  const [signupBonus, setSignupBonus] = useState<number>(0);
  const [showConfirm, setShowConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'user',
    level: 'basic',
    isActive: true,
    isVerified: false
  });

  // Fetch signup bonus from settings
  useEffect(() => {
    if (!token) return;
    const fetchBonus = async () => {
      try {
        const res = await fetch(`${config.api.baseUrl}/api/settings/admin/list`, {
          headers: {
            'Content-Type': 'application/json',
            [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
            [config.auth.headers.auth]: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          const settings = Array.isArray(data.data) ? data.data : data.data?.settings || [];
          const bonus = settings.find((s: any) => s.key === 'signup_bonus_credits');
          if (bonus) setSignupBonus(parseInt(bonus.value) || 0);
        }
      } catch {}
    };
    fetchBonus();
  }, [token]);

  if (!user) {
    return null;
  }

  const canManageUsers = hasRole(user, ['admin.tenant', 'admin.org', 'admin.system']);

  if (!canManageUsers) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">
                    You don't have permission to create users.
                  </p>
                  <Button asChild>
                    <a href={route('/user/admin')}>Back to User Admin</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Reset level when role changes and set appropriate default
      if (field === 'role') {
        switch (value) {
          case 'user':
            updated.level = 'basic';
            break;
          case 'moderator':
            updated.level = 'review';
            break;
          case 'admin':
            updated.level = 'tenant';
            break;
          default:
            updated.level = 'basic';
        }
      }

      return updated;
    });
    setError(null);
    setSuccess(null);
  };

  // Get available levels based on selected role
  const getAvailableLevels = (role: string) => {
    switch (role) {
      case 'user':
        return [
          { value: 'basic', label: 'Basic' },
          { value: 'pro', label: 'Pro' },
          { value: 'max', label: 'Max' }
        ];
      case 'moderator':
        return [
          { value: 'review', label: 'Review' },
          { value: 'approve', label: 'Approve' },
          { value: 'manage', label: 'Manage' }
        ];
      case 'admin':
        const adminLevels = [
          { value: 'tenant', label: 'Tenant' }
        ];
        if (hasRole(user, ['admin.org', 'admin.system'])) {
          adminLevels.push({ value: 'org', label: 'Organization' });
        }
        if (hasRole(user, ['admin.system'])) {
          adminLevels.push({ value: 'system', label: 'System' });
        }
        return adminLevels;
      default:
        return [{ value: 'basic', label: 'Basic' }];
    }
  };

  // Password generation functions
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const setPasswordOption = (option: 'random' | 'default' | 'phone') => {
    let newPassword = '';
    switch (option) {
      case 'random':
        newPassword = generateRandomPassword();
        break;
      case 'default':
        newPassword = 'default12345';
        break;
      case 'phone':
        newPassword = formData.phone || '';
        break;
    }
    handleInputChange('password', newPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (!formData.email) {
      setError('Email is required');
      return;
    }

    // Show confirmation dialog
    setShowConfirm(true);
  };

  const confirmCreate = async () => {
    setShowConfirm(false);
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${config.api.baseUrl}/api/user/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
          [config.auth.headers.auth]: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const roleName = formData.role === 'admin' ? 'System Admin' : formData.role === 'moderator' ? 'Moderator' : 'User';
      setSuccess(`${formData.name || formData.email} created as ${roleName} with ₹${signupBonus} signup credits`);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'user',
        level: 'basic',
        isActive: true,
        isVerified: false
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthGuard requiredRoles={USER_ROLES.ADMIN_ACCESS}>
      <PageLayout size="full">
      <SEO
        title="Create User"
        description="Create a new user account"
      />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Create User
              </h1>
              <p className="text-muted-foreground">
                Add a new user to the system. Your role:{' '}
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {user.role}.{user.level}
                </span>
              </p>
            </div>
            <Button asChild variant="outline" className="gap-2">
              <a href={route('/user/admin')}>
                <ArrowLeft className="h-4 w-4" />
                Back to User Admin
              </a>
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                User Details
              </CardTitle>
              <CardDescription>
                Enter the information for the new user account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert className="mb-6 bg-destructive/10 border-destructive text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="mb-6 bg-green-50 border-green-200 text-green-800">
                  <CheckCircle className="h-4 w-4" />
                  <AlertTitle>Success</AlertTitle>
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">Full Name</label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">Email Address</label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-medium">Phone Number</label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter phone number (optional)"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium">Password</label>
                    <div className="space-y-2">
                      <Input
                        id="password"
                        type="text"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Enter password or use options below"
                        required
                      />
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPasswordOption('random')}
                        >
                          Random
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPasswordOption('default')}
                        >
                          Default12345
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPasswordOption('phone')}
                          disabled={!formData.phone || formData.phone.length < 6}
                        >
                          Phone Number
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label htmlFor="role" className="text-sm font-medium">Role</label>
                      <button
                        type="button"
                        onClick={() => setShowRbac(true)}
                        title="Learn about roles"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#FFD93D', padding: 0, display: 'flex', alignItems: 'center',
                        }}
                      >
                        <Info style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                    <Select
                      value={`${formData.role}.${formData.level}`}
                      onValueChange={(value) => {
                        const [role, level] = value.split('.');
                        setFormData(prev => ({ ...prev, role, level }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user.basic">User</SelectItem>
                        <SelectItem value="moderator.manage">Moderator</SelectItem>
                        {hasRole(user, ['admin.system']) && (
                          <SelectItem value="admin.system">System Admin</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="status" className="text-sm font-medium">Status</label>
                      <Select
                        value={formData.isActive ? 'active' : 'inactive'}
                        onValueChange={(value) => handleInputChange('isActive', value === 'active')}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="verification" className="text-sm font-medium">Email Verification</label>
                      <Select
                        value={formData.isVerified ? 'verified' : 'not-verified'}
                        onValueChange={(value) => handleInputChange('isVerified', value === 'verified')}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select verification status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="not-verified">Not Verified</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {signupBonus > 0 && (
                    <div className="col-span-1 md:col-span-2" style={{
                      background: 'rgba(255,217,61,0.06)', border: '1px solid rgba(255,217,61,0.2)',
                      borderRadius: 10, padding: '12px 16px',
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <Coins style={{ width: 18, height: 18, color: '#FFD93D', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: '#ccc' }}>
                        This user will receive <span style={{ color: '#FFD93D', fontWeight: 600 }}>₹{signupBonus}</span> signup credits on creation.{' '}
                        <a href={route('/admin/settings')} style={{ color: '#888', fontSize: 12, textDecoration: 'underline' }}>Configure in App Settings</a>
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button type="submit" disabled={isLoading} className="gap-2">
                    <UserPlus className="h-4 w-4" />
                    {isLoading ? 'Creating User...' : 'Create User'}
                  </Button>
                  <Button type="button" variant="outline" asChild>
                    <a href={route('/user/admin')}>Cancel</a>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
          </div>
        </div>
      </PageLayout.Content>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <>
          <div onClick={() => setShowConfirm(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999,
            backdropFilter: 'blur(4px)',
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: '#141414', border: '1px solid #2a2a2a', borderRadius: 14,
            width: '100%', maxWidth: 440, zIndex: 1000, padding: '24px',
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>Confirm User Creation</h3>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 20px' }}>Please review the details before creating this account.</p>

            <div style={{ background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '8px 12px', fontSize: 13 }}>
                <span style={{ color: '#666', fontWeight: 600 }}>Name</span>
                <span style={{ color: '#ccc' }}>{formData.name || '—'}</span>
                <span style={{ color: '#666', fontWeight: 600 }}>Email</span>
                <span style={{ color: '#ccc' }}>{formData.email}</span>
                <span style={{ color: '#666', fontWeight: 600 }}>Role</span>
                <span style={{ color: '#ccc' }}>
                  {formData.role === 'admin' ? 'System Admin' : formData.role === 'moderator' ? 'Moderator' : 'User'}
                </span>
                <span style={{ color: '#666', fontWeight: 600 }}>Status</span>
                <span style={{ color: formData.isActive ? '#22c55e' : '#ef4444' }}>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {signupBonus > 0 && (
              <div style={{
                background: 'rgba(255,217,61,0.08)', border: '1px solid rgba(255,217,61,0.25)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'rgba(255,217,61,0.15)', border: '1px solid rgba(255,217,61,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}>
                  ₹
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#FFD93D', margin: 0 }}>
                    ₹{signupBonus} signup credits will be allocated
                  </p>
                  <p style={{ fontSize: 12, color: '#888', margin: '2px 0 0' }}>
                    As per system settings, this user will receive {signupBonus} credits on creation
                  </p>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: '#222', border: '1px solid #333', color: '#999', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmCreate}
                style={{
                  padding: '8px 24px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: '#FFD93D', border: 'none', color: '#111', cursor: 'pointer',
                }}
              >
                Create User
              </button>
            </div>
          </div>
        </>
      )}

      {showRbac && <RbacPopup onClose={() => setShowRbac(false)} />}
      </PageLayout>
    </AuthGuard>
  );
};

export default CreateUserPage;